import { useEffect, useRef, useState } from "react";
import { UseFormReturn } from "react-hook-form";
import { DealInformationType } from "./DealInformationConfig";
import { DealType } from "../../utils/CommonVariables";
import { TenancyDeliveryDealType } from "./FitoutLandlordWorks/Config";

export const FEES_CLEARED_MESSAGE =
  "Tenancy coordination and design review fees were cleared because of the deal type.";

const isFeeFreeDealType = (label?: string) =>
  label === DealType.NewLeaseChangeOwnership;

const isFeeFreeDeliveryType = (label?: string) =>
  label === TenancyDeliveryDealType.NO_WORK ||
  label === TenancyDeliveryDealType.ATM_LICENCE;

/**
 * Clears the tenancy coordination and design review fees when the user changes
 * the deal type or tenancy delivery deal type to one that has no such fees.
 * Nothing is cleared when the deal loads already set to one of them, and
 * changing back never restores the fees.
 * Returns true while the "fees were cleared" notice should show.
 */
export const useClearFeesOnTypeChange = (
  form: UseFormReturn<DealInformationType, object>,
  dataLoaded: boolean
): boolean => {
  const dealType = form.watch("dealType.dealType.label");
  const deliveryType = form.watch(
    "fitoutLandlordWorks.tenancyDeliveryDealType.label"
  );
  const previous = useRef<{ dealType?: string; deliveryType?: string }>();
  const [feesCleared, setFeesCleared] = useState(false);

  useEffect(() => {
    if (!dataLoaded) return;

    const before = previous.current;
    previous.current = { dealType, deliveryType };
    // First pass after loading only records what the deal already has
    if (!before) return;

    const dealTypeChanged = dealType !== before.dealType;
    const deliveryTypeChanged = deliveryType !== before.deliveryType;
    if (!dealTypeChanged && !deliveryTypeChanged) return;

    const shouldClear =
      (dealTypeChanged && isFeeFreeDealType(dealType)) ||
      (deliveryTypeChanged && isFeeFreeDeliveryType(deliveryType));

    if (shouldClear) {
      form.setValue("charges.tenancyCoordinationFee", null, {
        shouldValidate: true,
        shouldDirty: true,
      });
      form.setValue("charges.openingPromotionLevyFees", null, {
        shouldValidate: true,
        shouldDirty: true,
      });
    }
    setFeesCleared(shouldClear);
  }, [dataLoaded, dealType, deliveryType, form]);

  return feesCleared;
};
